import { Component, ChangeDetectionStrategy, type OnInit, inject } from '@angular/core';
import { CdkScrollable } from '@angular/cdk/overlay';
import { AsyncPipe } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { take, type Observable } from 'rxjs';

import { EDrawerType } from '@common/types/layout.type';

import { environment } from '@web-env/environment';

import { WebDeviceToggleComponent } from '@web/shared/components/web-device-toggle/web-device-toggle.component';
import { WebEditorComponent } from '@web/shared/components/web-editor/web-editor.component';

import { AppFacade } from '@web/store/app/app.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { ContentFacade } from '@web/store/website/content/content.facade';
import { AiSessionService } from '@web/store/page-structure/ai-connector/ai-session.service';

@Component({
    selector: 'web-content',
    templateUrl: './content.component.html',
    styleUrls: ['./content.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        WebDeviceToggleComponent,
        TooltipModule,
        RouterOutlet,
        WebEditorComponent,
        AsyncPipe,
        TranslateModule,
        CdkScrollable,
    ],
})
export class ContentComponent implements OnInit {
    private readonly appFacade: AppFacade = inject(AppFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly contentFacade: ContentFacade = inject(ContentFacade);
    private readonly aiSession: AiSessionService = inject(AiSessionService);

    /**
     * Blocks the canvas while the assistant is working — §7 of `ai-mcp-plan.md`.
     *
     * Not merely cosmetic: the reply's edits are applied server-side and the whole page is re-read
     * when it ends, so an edit made here in the meantime is overwritten without a trace. The overlay
     * is scoped to the canvas (see the LESS), leaving the chat itself usable — the user has to be
     * able to read the reply and stop it.
     */
    public readonly isAiSessionActive = this.aiSession.isActive;

    public readonly isAiChatEnabled: boolean = environment.aiChat;

    public readonly isReady$: Observable<boolean> = this.appFacade.isReady$;

    public readonly isDeviceMobile$: Observable<boolean> = this.layoutFacade.isDeviceMobile$;
    public readonly isDeviceTablet$: Observable<boolean> = this.layoutFacade.isDeviceTablet$;

    public readonly isContentLoading$: Observable<boolean> = this.contentFacade.isLoading$;

    public isGlobalLoading: boolean = true;

    public ngOnInit(): void {
        this.isReady$.subscribe((isReady: boolean) => {
            if (isReady) {
                setTimeout(() => {
                    this.isGlobalLoading = false;
                }, 100);
            }
        });
    }

    public showAiChatDrawer(): void {
        this.layoutFacade.showAiChatDrawer$.pipe(take(1)).subscribe((value) => {
            if (value) {
                this.layoutFacade.closeDrawer();
                return;
            }

            this.layoutFacade.openDrawer({ drawerType: EDrawerType.aiChat });
        });
    }
}
